import '@design-system/link/dist/cjs/index.css';

import { Icon } from '@design-system/icon';
import { LinkProps } from '@design-system/link';
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import { useLocation } from '@reach/router';
import classNames from 'classnames';
import { Link, navigate } from 'gatsby';
import scrollTo from 'gatsby-plugin-smoothscroll';
import React, { ReactElement, useContext } from 'react';

import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';

const TMPLinkWrap = ({
  variant = 'primary',
  size = 'small',
  children,
  className,
  iconPrefix,
  iconSuffix,
  href,
  onClick,
  ...htmlProperties
}: LinkProps): ReactElement => {
  const IconPrefixComponent = iconPrefix?.component || Icon;
  const IconSuffixComponent = iconSuffix?.component || Icon;
  const { pathname } = useLocation();

  const { isMobile } = useContext(ScreenRecognitionContext);
  const { displayModal } = useContext(ExternalLinkDialogContext);

  const isInternal = /(^\/(?!\/))|(^#)/.test(href);
  const commonProps = {
    className: classNames(
      'cds-link',
      className,
      `cds-link--${variant}`,
      `cds-link--${variant}-${size}`
    ),
    ...htmlProperties,
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) onClick(event);
    if (isMobile || !href.includes('#')) return;

    event.preventDefault();
    const isHomePage = pathname === '/';

    const id = href.replace(/(^\/)|(\/$)/g, '');
    scrollTo(id);
    navigate(isHomePage ? id : href);
  };

  const content = (
    <>
      {iconPrefix?.props && (
        <IconPrefixComponent
          className={classNames('icon__prefix', `icon--${variant}-icon`)}
          {...iconPrefix.props}
        />
      )}
      <span
        className={classNames('cds-link__inner', {
          'cds-sr-only': variant.includes('-icon'),
        })}
      >
        {children}
      </span>
      {iconSuffix?.props && (
        <IconSuffixComponent
          className={classNames('icon__suffix', `icon--${variant}-icon`)}
          {...iconSuffix.props}
        />
      )}
    </>
  );

  return isInternal ? (
    <Link to={href} {...(commonProps as any)} onClick={handleClick}>
      {content}
    </Link>
  ) : (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => displayModal(e, href, onClick)}
      {...commonProps}
    >
      {content}
    </a>
  );
};

export default TMPLinkWrap;
